import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { hostFollowApi } from '../services/hostFollowApi';

interface Props {
  hostId: string;
  /** Primary sits in the host page header; secondary sits inside a host card. */
  variant?: 'primary' | 'secondary';
  className?: string;
}

const CONFIRMATION_MS = 4000;

/**
 * Follow/unfollow control for a host.
 *
 * The router reuses this element when only `hostId` changes (for example
 * `/activities?hostId=a` to `?hostId=b`), and every piece of instance state
 * here belongs to one specific host: the loaded status, an in-flight request,
 * the confirmation message and its timer. Keying the inner component on
 * `hostId` remounts it on a host change so none of that can
 * follow the button to a host it does not describe. It is done here rather than
 * asking each call site for a `key` so a caller cannot forget it.
 */
export default function FollowHostButton(props: Props) {
  return <HostFollowControl key={props.hostId} {...props} />;
}

function HostFollowControl({ hostId, variant = 'secondary', className }: Props) {
  const { t } = useTranslation('hostFollow');
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [followed, setFollowed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const confirmationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Bumped every time the user acts, so a status response issued earlier can be
  // recognised as stale and dropped instead of undoing the mutation.
  const mutationCount = useRef(0);

  const flash = useCallback((message: string) => {
    setConfirmation(message);
    if (confirmationTimer.current) clearTimeout(confirmationTimer.current);
    confirmationTimer.current = setTimeout(() => setConfirmation(''), CONFIRMATION_MS);
  }, []);

  useEffect(
    () => () => {
      if (confirmationTimer.current) clearTimeout(confirmationTimer.current);
    },
    []
  );

  // Current state. Signed-out visitors see the unfollowed face; the real
  // state is only knowable once they log in.
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setFollowed(false);
      return;
    }
    // This effect can run more than once per host (auth resolving), and a click
    // can land while a load is in flight, so an earlier load must not overwrite
    // a later answer or undo a mutation.
    let superseded = false;
    const issuedAt = mutationCount.current;
    const applyIfFresh = (apply: () => void) => {
      if (!superseded && mutationCount.current === issuedAt) apply();
    };

    hostFollowApi
      .getStatus(hostId)
      .then((value) => applyIfFresh(() => setFollowed(value)))
      .catch(() => applyIfFresh(() => setFollowed(false)));

    return () => {
      superseded = true;
    };
  }, [authLoading, isAuthenticated, hostId]);

  const pending = busy || authLoading;

  const handleClick = async () => {
    if (pending) return;
    if (!isAuthenticated) {
      // Send them to log in and back to this exact page, the same way joining a
      // waitlist does. The click is not remembered: they follow once they are
      // back, which keeps this button out of the business of replaying an
      // action across an auth change.
      const target = `${window.location.pathname}${window.location.search}`;
      navigate(`/login?redirect=${encodeURIComponent(target)}`);
      return;
    }

    const previous = followed === true;
    mutationCount.current += 1;
    setBusy(true);
    setFollowed(!previous); // optimistic
    try {
      if (previous) {
        await hostFollowApi.unfollow(hostId);
        flash(t('unfollowedToast'));
      } else {
        await hostFollowApi.follow(hostId);
        flash(t('followedToast'));
      }
    } catch {
      setFollowed(previous); // roll back
      flash(t('actionError'));
    } finally {
      setBusy(false);
    }
  };

  const isFollowing = followed === true;
  const showUnfollow = isFollowing && hovering;
  const label = isFollowing ? (showUnfollow ? t('unfollow') : t('followed')) : t('follow');

  const base =
    'inline-flex items-center gap-2 rounded-lg font-semibold transition-colors disabled:opacity-60';
  const size = variant === 'primary' ? 'px-5 py-2.5 text-sm' : 'px-4 py-2 text-sm';
  const tone = isFollowing
    ? 'border border-primary/40 bg-white text-primary hover:border-primary hover:bg-primary/5'
    : variant === 'primary'
      ? 'bg-primary text-white hover:bg-primary/90'
      : 'border border-primary/40 bg-white text-primary hover:bg-primary/5';

  const Icon = pending ? Loader2 : showUnfollow ? BellOff : isFollowing ? BellRing : Bell;

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handleClick}
        disabled={pending}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onFocus={() => setHovering(true)}
        onBlur={() => setHovering(false)}
        aria-pressed={isFollowing}
        title={t('followHint')}
        className={`${base} ${size} ${tone}`}
      >
        <Icon className={`h-4 w-4 ${pending ? 'animate-spin' : ''}`} />
        {label}
      </button>
      {confirmation && <p className="mt-2 text-xs text-charcoal/70">{confirmation}</p>}
    </div>
  );
}
